import {
  AspectRatio,
  Box,
  Center,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
  Image,
  Heading,
  IconButton,
  Icon,
  View,
  Spinner,
  Button,
} from "native-base";
import React, { useEffect, useState } from "react";
import GuestLayout from "../components/GuestLayout";
import Header from "../components/Header";
import { MaterialIcons } from "@native-base/icons";
import { useHistory } from "react-router-dom";
import { compose } from "redux";
import { connect, shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { isLoaded, useFirestoreConnect } from "react-redux-firebase";

// Firestore "in" filters accept at most 10 values in the v8 SDK.
const IN_QUERY_LIMIT = 10;
// "__name__" is FieldPath.documentId() as a string, so the query config stays serialisable.
const DOC_ID_FIELD = "__name__";

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const Home = ({ auth, isDefult }) => {
  useEffect(() => { document.title = "Dashboard | Piatto"; }, []);
  const history = useHistory();
  const dispatch = useDispatch();
  // RRF auto-syncs VenueManagement/{uid} to state.firebase.profile (configured in store.js).
  const profile = useSelector(({ firebase }) => firebase.profile);
  const venueIds = profile?.venueIds || [];
  const brandIds = profile?.brandIds || [];

  // Fetch venues: individually-claimed venues by Firestore doc ID, brand venues by brandId.
  // venueIds are doc IDs. Many venues have a `bizId` field that differs from
  // their doc ID (e.g. "google_ChIJ…"), so we must not query on that field.
  const venueIdChunks = chunk(venueIds, IN_QUERY_LIMIT);
  const queries = venueIdChunks.map((ids, i) => ({
    collection: "Business",
    where: [[DOC_ID_FIELD, "in", ids]],
    storeAs: `myVenues_${i}`,
  }));
  if (brandIds.length > 0) {
    queries.push({ collection: "Business", where: [["brandId", "in", brandIds]], storeAs: "myBrandVenues" });
  }
  useFirestoreConnect(queries.length > 0 ? queries : []);

  const individualVenueChunks = useSelector(({ firestore: { ordered } }) =>
    venueIdChunks.map((_, i) => ordered[`myVenues_${i}`]),
    shallowEqual
  );
  const individualVenues = individualVenueChunks.flatMap((c) => c || []);
  const brandVenues = useSelector(({ firestore: { ordered } }) => ordered.myBrandVenues || []);

  // Merge and deduplicate on doc ID
  const seen = new Set();
  const BIZ = [];
  for (const v of [...individualVenues, ...brandVenues]) {
    if (v && v.id && !seen.has(v.id)) {
      seen.add(v.id);
      BIZ.push(v);
    }
  }

  const [searchTerm, setSearchTerm] = useState("");

  BIZ.sort((a, b) => (a.bizName || "").localeCompare(b.bizName || ""));

  const filteredBIZ = searchTerm
    ? BIZ.filter(v =>
        (v.bizName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.bizSuburb || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.bizAddress || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : BIZ;

  const dataLoaded = isLoaded(profile)
    && individualVenueChunks.every((c) => isLoaded(c))
    && (brandIds.length === 0 || isLoaded(brandVenues));

  if (!auth.uid) return <Redirect to="/" />;
  if (isDefult) return <Redirect to={isDefult} />;

  if (dataLoaded) {
    return (
      <>
        <VStack
          role="main"
          bg="coolGray.800"
          zIndex={2}
          px={{ base: 4, md: 6 }}
          py={{ base: 4, md: 6 }}
          borderBottomRadius="25"
        >
          <VStack space="2">
            <Text
              fontSize="2xl"
              color="coolGray.50"
              fontWeight="semibold"
              letterSpacing="0.2"
            >
              Welcome to Piatto
            </Text>
            <Text
              fontSize="sm"
              color="coolGray.50"
              fontWeight="normal"
              letterSpacing="0.2"
            >
              Piatto is enhancing accessibility to hospitality. The Piatto app
              helps venues accommodate every guest — from accessible ordering
              and dietary management to seamless dine-in, pickup and curbside
              experiences. For businesses that wish to join the platform,
              please add your business by clicking the plus sign below and
              completing the subsequent form.
            </Text>
          </VStack>
        </VStack>
        {BIZ.length > 1 && (
          <Box px={4} pt={4} alignItems="center">
            <Box maxW="500px" w="full" mx="auto">
              <Input
                placeholder="Search venues by name or location..."
                value={searchTerm}
                onChangeText={setSearchTerm}
                InputLeftElement={
                  <Icon as={MaterialIcons} name="search" size="sm" ml={2} color="coolGray.400" />
                }
                InputRightElement={
                  searchTerm ? (
                    <IconButton
                      icon={<Icon as={MaterialIcons} name="close" size="sm" />}
                      onPress={() => setSearchTerm("")}
                      variant="ghost"
                      size="sm"
                      mr={1}
                    />
                  ) : null
                }
                bg="white"
                borderRadius="lg"
                fontSize="md"
              />
              <Text fontSize="xs" color="coolGray.400" mt={1} textAlign="center">
                {filteredBIZ.length} of {BIZ.length} venues
              </Text>
            </Box>
          </Box>
        )}
        <Box justifyContent="center" alignItems="center" mt={5}>
          <VStack px="4" my={{ base: "10", md: "1" }}>
            <HStack flexWrap="wrap" alignItems="center" justifyContent="center">
              <Box m={2} textAlign={"center"} alignItems="center">
                <Box
                  maxW="80"
                  minH={"150px"}
                  justifyContent="center"
                  rounded="lg"
                  overflow="hidden"
                  borderColor="coolGray.200"
                  borderWidth="1"
                  _web={{
                    shadow: 2,
                    borderWidth: 0,
                  }}
                  _light={{
                    backgroundColor: "gray.50",
                  }}
                >
                  <Stack p="4" space={3}>
                    <Stack space={2}>
                      <Heading size="md" ml="-1">
                        Add Your Business
                      </Heading>
                      <Text
                        fontSize="xs"
                        _light={{
                          color: "violet.500",
                        }}
                        _dark={{
                          color: "violet.400",
                        }}
                        fontWeight="500"
                        ml="-0.5"
                        mt="-1"
                      >
                        Click on plus Button
                      </Text>
                      <IconButton
                        onPress={() => {
                          history.push("/AddBusiness")
                        }}
                        variant="solid"
                        colorScheme={"blueGray"}
                        accessibilityLabel="Add your business"
                        icon={
                          <Icon
                            as={MaterialIcons}
                            name="add"
                            size="2xl"
                            color="white"
                          />
                        }
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Box>
              {filteredBIZ &&
                filteredBIZ.map(function(item) {
                  const image = item.bizPic1 || item.bizPic2 || item.bizPic3 || null;
                  return (
                    <Box m={2} textAlign={"center"} alignItems="center" key={item.id}>
                      <Box
                        w="80"
                        rounded="lg"
                        overflow="hidden"
                        borderColor="coolGray.200"
                        borderWidth="1"
                        _web={{
                          shadow: 2,
                          borderWidth: 0,
                        }}
                        _light={{
                          backgroundColor: "gray.50",
                        }}
                      >
                        {image ? (
                          <AspectRatio ratio={16 / 9}>
                            <Image
                              source={{ uri: image }}
                              alt={item.bizName}
                              resizeMode="cover"
                            />
                          </AspectRatio>
                        ) : (
                          <Box bg="coolGray.200" h="120px" justifyContent="center" alignItems="center">
                            <Icon as={MaterialIcons} name="store" size="4xl" color="coolGray.400" />
                          </Box>
                        )}
                        <Stack p="4" space={3}>
                          <Stack space={2}>
                            <Heading
                              textTransform={"capitalize"}
                              size="md"
                              ml="-1"
                            >
                              {item.bizName}
                            </Heading>
                            <Text fontSize="xs" color="coolGray.500" mt="-1">
                              {item.bizSuburb || item.bizAddress || ""}
                            </Text>
                            <Button
                              onPress={() => history.push(`/Orders/${item.id}`)}
                              variant="solid"
                              colorScheme={"blueGray"}
                              rightIcon={
                                <Icon
                                  as={MaterialIcons}
                                  name="open-in-browser"
                                  color="white"
                                />
                              }
                            >
                              Orders || Manage
                            </Button>
                            <Button
                              onPress={() =>
                                dispatch({
                                  type: "SETDEFAULT",
                                  payload: `/Orders/${item.id}`,
                                })
                              }
                              variant="solid"
                              colorScheme={"blueGray"}
                              rightIcon={
                                <Icon
                                  as={MaterialIcons}
                                  name="star-outline"
                                  color="white"
                                />
                              }
                            >
                              Set default
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
            </HStack>
          </VStack>
        </Box>
      </>
    );
  } else {
    return (
      <View role="status" aria-live="polite">
        <Center>
          <Spinner size={"lg"} accessibilityLabel="Loading" />
        </Center>
      </View>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    auth: state.firebase.auth,
    isDefult: state.state.setDefault,
  };
};

export default compose(connect(mapStateToProps))(Home);
